import { type FC, useCallback, useEffect } from 'react';
import { batch } from 'react-redux';

import { useGetAnalyticsPage } from '@/app/shared/hooks/useGetAnalyticsPage';

import { MAP_ZOOMING_OPTIONS } from '@/microfrontends/map/shared/constants/map';
import { useMapFeaturesContext } from '@/microfrontends/map/shared/context/MapLayersCenterContext';
import { PinService } from '@/microfrontends/map/shared/services/PinService';
import { ViewedClustersService } from '@/microfrontends/map/shared/services/ViewedClustersService';
import { type HotspotProperties } from '@/microfrontends/map/shared/types/Map';
import { PIN_EXTENDED_TYPES } from '@/microfrontends/map/shared/utils/mapModules/PinsLayer/types';

import { useGetClustersQuery } from '@/packages/query-state/shared/store/search-offers-index/v2/get-clusters-for-map/query';
import { nextZoomSelector } from '@/packages/query-state/shared/store/search-offers-index/v2/get-clusters-for-map/selectors';
import { type Cluster } from '@/packages/query-state/shared/types/clusters/ClustersResponse';
import { useConstant } from '@/packages/react-utils/useConstant';
import { useScrollToTopWithCallback } from '@/packages/react-utils/useScrollToTopWithCallback';
import { boundActionsFactory } from '@/packages/redux-state/store';

import { useMapStoreContext } from '../../context/mapStoreContext';

import { trackMapPinClick } from './internal/tracking';

const useBoundActions = boundActionsFactory(reduxActions => ({
  setActivePin: reduxActions.mapControls.setActivePin,
  setViewMode: reduxActions.listingLayout.setViewMode,
}));

export const MapPinsClickHandlers: FC = () => {
  const { map } = useMapStoreContext();
  const { hotspotsLayer, balloonManager } = useMapFeaturesContext();
  const boundActions = useBoundActions();
  const viewedClustersService = useConstant(() => new ViewedClustersService());
  const { data: nextZoom } = useGetClustersQuery({ select: nextZoomSelector });
  const analyticsPage = useGetAnalyticsPage();
  const scrollToTopWithCallback = useScrollToTopWithCallback();

  const handleHotspotClick = useCallback(
    (event: YMaps.IEvent) => {
      const activeObject = event.get<YMaps.IHotspotLayerObject<HotspotProperties>>('activeObject');
      const { clusterData } = activeObject.getProperties();
      const pinService = new PinService({ cluster: clusterData });
      const extendedTypes = pinService.provideExtendedTypes();
      const withBalloon = extendedTypes.has(PIN_EXTENDED_TYPES.withBalloon);
      const isViewed = extendedTypes.has(PIN_EXTENDED_TYPES.viewed);
      const currentZoom = Math.round(map.getZoom());

      /**
       * Мы не приближаем карту, если зум интерактивный (когда пины кликабельны),
       * либо если был клик на пин с балуном конкретно в сам пин
       */
      if (withBalloon || pinService.isPinInteractive) {
        if (!isViewed) {
          viewedClustersService.storeViewedClustersIds([clusterData.id]);
        }

        batch(() => {
          /**
           * При клике на пин меняем режим отображения
           */
          boundActions.setViewMode('hybrid');

          /**
           * При клике на кластер, если страница проскроллена вниз, мы скроллим ее вверх для лучшего UX.
           * В стор кладем обязательно копию кластера, иначе ошибка на уровне карты.
           */
          scrollToTopWithCallback(() => {
            /**
             */
            boundActions.setActivePin(JSON.parse(JSON.stringify(clusterData)));

            /**
             * Трекаем событие только для интерактивных пинов!
             */
            trackMapPinClick({ page: analyticsPage, pinService, zoom: currentZoom });
          });
        });

        return;
      }

      const [leftTop, bottomRight] = clusterData.geometry.coordinates;
      const center: YMaps.TCoord = [leftTop, bottomRight];

      map.setCenter(center, nextZoom, MAP_ZOOMING_OPTIONS);

      return;
    },
    [map, nextZoom, viewedClustersService, boundActions, analyticsPage],
  );

  const handleBalloonClick = useCallback(
    (event: YMaps.IEvent) => {
      const objectId = event.get<string>('objectId');
      const clusterData = balloonManager.objectManager.objects.getById<Cluster>(objectId);
      const pinService = new PinService({ cluster: clusterData });
      const extendedTypes = pinService.provideExtendedTypes();
      const isViewed = extendedTypes.has(PIN_EXTENDED_TYPES.viewed);
      const currentZoom = Math.round(map.getZoom());

      trackMapPinClick({ page: analyticsPage, pinService, zoom: currentZoom });

      if (!isViewed) {
        viewedClustersService.storeViewedClustersIds([clusterData.id]);
      }

      batch(() => {
        /**
         * При клике на пин меняем режим отображения
         */
        boundActions.setViewMode('hybrid');

        scrollToTopWithCallback(() => {
          /**
           * При клике на кластер, если страница проскроллена вниз, мы скроллим ее вверх для лучшего UX.
           * В стор кладем обязательно копию кластера, иначе ошибка на уровне карты.
           */
          boundActions.setActivePin(JSON.parse(JSON.stringify(clusterData)));
        });
      });
    },
    [balloonManager.objectManager.objects, map, analyticsPage, viewedClustersService, boundActions],
  );

  useEffect(() => {
    hotspotsLayer.layer.events.add('click', handleHotspotClick);
    balloonManager.objectManager.objects.events.add('click', handleBalloonClick);

    return (): void => {
      hotspotsLayer.layer.events.remove('click', handleHotspotClick);
      balloonManager.objectManager.objects.events.remove('click', handleBalloonClick);
    };
  }, [balloonManager.objectManager, hotspotsLayer.layer, handleBalloonClick, handleHotspotClick]);

  return null;
};
